/**
 * Scrollers with drawn thumbs — the WIDEO and mobile carousels, and the
 * NA CZASIE and ROZMOWY side panels.
 *
 * Markup contract (views/home/*.php):
 *   [data-scroll="x|y"]      root; gets is-start / is-end / is-scrollable
 *     [data-scroll-track]    the native scroller (scrollbar hidden in CSS)
 *     [data-scroll-rail]     box the thumb travels in (optional: the track)
 *     [data-scroll-thumb]    drawn thumb
 *     [data-scroll-prev|next] optional arrow buttons
 *
 * The thumb keeps its Figma size and is only translated, so at scroll 0 the
 * page is exactly the design. Travel keeps the thumb's resting inset at both
 * ends (the WIDEO thumb sits 18px into its bar, the ROZMOWY thumb 15px into
 * the list).
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

// Pixels of pointer travel before a mouse press on a track becomes a drag.
const DRAG_THRESHOLD = 5;

function initScroller(root) {
  const x = root.dataset.scroll === 'x';
  const track = root.querySelector('[data-scroll-track]');
  const thumb = root.querySelector('[data-scroll-thumb]');
  if (!track) return;

  const rail = root.querySelector('[data-scroll-rail]') || track;
  const pos = x ? 'scrollLeft' : 'scrollTop';
  const client = (el) => (x ? el.clientWidth : el.clientHeight);
  const max = () => (x ? track.scrollWidth - track.clientWidth : track.scrollHeight - track.clientHeight);
  const coord = (e) => (x ? e.clientX : e.clientY);

  let travel = 0;
  let frame = 0;

  const measure = () => {
    if (!thumb || !client(rail)) return;
    thumb.style.transform = '';
    const t = thumb.getBoundingClientRect();
    const r = rail.getBoundingClientRect();
    const inset = x ? t.left - r.left - rail.clientLeft : t.top - r.top - rail.clientTop;
    const size = x ? t.width : t.height;
    travel = Math.max(0, client(rail) - size - 2 * Math.max(0, inset));
  };

  const update = () => {
    frame = 0;
    const m = max();
    const p = m > 0 ? Math.min(1, Math.max(0, track[pos] / m)) : 0;

    root.classList.toggle('is-scrollable', m > 1);
    root.classList.toggle('is-start', track[pos] <= 1);
    root.classList.toggle('is-end', track[pos] >= m - 1);

    if (thumb) {
      thumb.style.visibility = m > 1 ? '' : 'hidden';
      thumb.style.transform = p ? `translate${x ? 'X' : 'Y'}(${(p * travel).toFixed(2)}px)` : '';
    }
  };

  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };

  track.addEventListener('scroll', schedule, { passive: true });
  // ResizeObserver arrived in Safari 13.1; older ones fall back to window resizes.
  if ('ResizeObserver' in window) new ResizeObserver(() => { measure(); update(); }).observe(track);
  else window.addEventListener('resize', () => { measure(); update(); }, { passive: true });

  // Snapping fights both kinds of drag; restore it once the scroll settles.
  const releaseSnap = () => {
    const restore = () => { track.style.scrollSnapType = ''; };
    if ('onscrollend' in track) track.addEventListener('scrollend', restore, { once: true });
    setTimeout(restore, 400);
  };

  // ---- Drag the thumb (or press the rail) ----
  if (thumb) {
    thumb.style.touchAction = 'none';

    thumb.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || !travel) return;
      e.preventDefault();
      e.stopPropagation();
      thumb.setPointerCapture(e.pointerId);
      root.classList.add('is-dragging');
      track.style.scrollSnapType = 'none';

      const start = coord(e);
      const from = track[pos];
      const ratio = max() / travel;

      const move = (ev) => { track[pos] = from + (coord(ev) - start) * ratio; };
      const end = () => {
        thumb.removeEventListener('pointermove', move);
        root.classList.remove('is-dragging');
        track.style.scrollSnapType = '';
      };

      thumb.addEventListener('pointermove', move);
      thumb.addEventListener('pointerup', end, { once: true });
      thumb.addEventListener('pointercancel', end, { once: true });
    });

    if (rail !== track) {
      rail.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 || e.target === thumb || !travel) return;
        const r = rail.getBoundingClientRect();
        const at = (coord(e) - (x ? r.left : r.top)) / client(rail);
        track.scrollTo({ [x ? 'left' : 'top']: at * max(), behavior: behavior() });
      });
    }
  }

  // ---- Arrows: one item per press, wrapping at both ends ----
  const step = () => {
    const [a, b] = track.children;
    if (!a) return client(track);
    return b ? (x ? b.offsetLeft - a.offsetLeft : b.offsetTop - a.offsetTop) : (x ? a.offsetWidth : a.offsetHeight);
  };

  const go = (dir) => {
    const m = max();
    const key = x ? 'left' : 'top';
    if (dir > 0 && track[pos] >= m - 1) track.scrollTo({ [key]: 0, behavior: behavior() });
    else if (dir < 0 && track[pos] <= 1) track.scrollTo({ [key]: m, behavior: behavior() });
    else track.scrollBy({ [key]: dir * step(), behavior: behavior() });
  };

  root.querySelector('[data-scroll-prev]')?.addEventListener('click', () => go(-1));
  root.querySelector('[data-scroll-next]')?.addEventListener('click', () => go(1));

  // ---- Mouse drag on the track itself (touch and trackpads scroll natively) ----
  let press = null;

  track.addEventListener('dragstart', (e) => e.preventDefault());

  track.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || max() <= 1) return;
    press = { id: e.pointerId, start: coord(e), from: track[pos], dragging: false };
  });

  track.addEventListener('pointermove', (e) => {
    if (!press || e.pointerId !== press.id) return;
    const delta = coord(e) - press.start;

    if (!press.dragging) {
      if (Math.abs(delta) < DRAG_THRESHOLD) return;
      // Capture only once it is a drag, so a plain click still reaches the link.
      press.dragging = true;
      track.setPointerCapture(e.pointerId);
      track.style.scrollSnapType = 'none';
      root.classList.add('is-dragging');
    }

    track[pos] = press.from - delta;
  });

  const endPress = () => {
    if (!press) return;
    const { dragging } = press;
    press = null;
    if (!dragging) return;

    root.classList.remove('is-dragging');
    // Settle on the nearest item, then hand control back to scroll-snap.
    const s = step();
    const target = Math.round(track[pos] / s) * s;
    track.scrollTo({ [x ? 'left' : 'top']: target, behavior: behavior() });
    releaseSnap();
  };

  track.addEventListener('pointerup', endPress);
  track.addEventListener('pointercancel', endPress);

  measure();
  update();
}

export function initScrollers(scope = document) {
  scope.querySelectorAll('[data-scroll]').forEach(initScroller);
}
